import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { PageHeader } from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Wallet, Loader2, RefreshCw, TrendingUp, TrendingDown, CheckCircle2, Clock, AlertTriangle, Sparkles, Filter, X as IconX,
} from "lucide-react";
import { toast } from "sonner";
import {
  BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, Legend,
  PieChart, Pie, Cell,
} from "recharts";
import LearnerHint from "@/components/LearnerHint";

const PERIODOS = [
  { v: "mes_actual", label: "Este mes" },
  { v: "mes_anterior", label: "Mes anterior" },
  { v: "trimestre", label: "Trimestre" },
  { v: "ytd", label: "Año actual" },
  { v: "anio_anterior", label: "Año anterior" },
];

const ESTADO_COLORS = {
  paid: "#10b981",
  pending: "#f59e0b",
  overdue: "#ef4444",
  voided: "#71717a",
  draft: "#a78bfa",
  overpaid: "#06b6d4",
  unknown: "#52525b",
};
const ESTADO_LABEL = {
  paid: "Cobrada",
  pending: "Pendiente",
  overdue: "Vencida",
  voided: "Anulada",
  draft: "Borrador",
  overpaid: "Sobrepagada",
  unknown: "Sin estado",
};

export default function Tesoreria() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [periodo, setPeriodo] = useState("mes_actual");
  const [clienteId, setClienteId] = useState("");
  const [clientes, setClientes] = useState([]);
  const [meta, setMeta] = useState(null);
  const [facturas, setFacturas] = useState([]);
  const [estadoFiltro, setEstadoFiltro] = useState("");
  const [viaFiltro, setViaFiltro] = useState("");

  const loadDashboard = async () => {
    setLoading(true);
    try {
      const params = { periodo };
      if (clienteId) params.cliente_id = clienteId;
      const [d, f] = await Promise.all([
        api.get("/tesoreria/dashboard", { params }),
        api.get("/tesoreria/facturas", { params: { ...params, ...(estadoFiltro ? { estado: estadoFiltro } : {}), ...(viaFiltro ? { via: viaFiltro } : {}), limit: 50 } }),
      ]);
      setData(d.data);
      setFacturas(f.data.items || []);
    } catch (e) {
      toast.error("Error cargando tesorería");
    } finally { setLoading(false); }
  };

  const loadAux = async () => {
    try {
      const [m, c] = await Promise.all([
        api.get("/tesoreria/last-refresh"),
        api.get("/tesoreria/clientes-options"),
      ]);
      setMeta(m.data);
      setClientes(c.data.items || []);
    } catch (e) { /* ignore */ }
  };

  const [fdHealth, setFdHealth] = useState(null);
  const [checkingHealth, setCheckingHealth] = useState(false);

  const checkFdHealth = async () => {
    setCheckingHealth(true);
    try {
      const { data } = await api.get("/facturadirecta/health");
      setFdHealth(data);
      if (data.connection?.ok) {
        toast.success(`FD conectada (${data.connection.ms}ms)`);
      } else if (!data.configured) {
        toast.error("FD no configurada: faltan API_KEY o COMPANY_ID en .env");
      } else {
        toast.error(`FD error: ${data.connection?.error || data.connection?.status_code || "desconocido"}`);
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error chequeando FD");
    } finally { setCheckingHealth(false); }
  };

  const doRefresh = async () => {
    setRefreshing(true);
    try {
      const { data } = await api.post("/tesoreria/refresh");
      toast.success(`Sincronizadas ${data.count} facturas en ${data.duration_ms}ms`);
      await loadAux();
      await loadDashboard();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error refrescando");
    } finally { setRefreshing(false); }
  };

  useEffect(() => { loadAux(); }, []);
  useEffect(() => { loadDashboard(); /* eslint-disable-next-line */ }, [periodo, clienteId, estadoFiltro, viaFiltro]);

  if (loading && !data) {
    return <div className="p-10 flex justify-center"><Loader2 className="w-8 h-8 animate-spin text-emerald-400" /></div>;
  }
  if (!data) return null;

  const k = data.kpis;
  const variation = k.facturado_periodo_anterior > 0
    ? ((k.total_facturado - k.facturado_periodo_anterior) / k.facturado_periodo_anterior) * 100
    : null;
  const cobroRatio = k.total_facturado > 0 ? (k.total_cobrado / k.total_facturado) * 100 : 0;

  return (
    <div className="p-4 md:p-6 space-y-6" data-testid="tesoreria-dashboard">
      <PageHeader title="Tesorería" subtitle="Facturación, cobros y vencimientos desde FacturaDirecta" icon={Wallet}>
        <div className="flex items-center gap-2 flex-wrap">
          {meta?.last_refresh_at && (
            <div className="text-[10px] text-zinc-500">
              Última sync: {new Date(meta.last_refresh_at).toLocaleString("es-ES", { dateStyle: "short", timeStyle: "short" })}
              {meta.count > 0 && <span> · {meta.count} facturas</span>}
            </div>
          )}
          <Button onClick={doRefresh} disabled={refreshing} size="sm" variant="outline" data-testid="btn-tesoreria-refresh">
            {refreshing ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <RefreshCw className="w-3 h-3 mr-1" />}
            Refrescar desde FD
          </Button>
        </div>
      </PageHeader>

      {/* Iter 88 — Aviso visible cuando la caché FD está vacía o desactualizada */}
      {(!meta?.last_refresh_at || (meta?.count ?? 0) === 0) && (
        <div className="bg-amber-500/10 border border-amber-500/40 rounded-xl p-4 flex items-start gap-3"
             data-testid="tesoreria-cache-empty">
          <AlertTriangle className="w-5 h-5 text-amber-400 mt-0.5 flex-shrink-0" />
          <div className="flex-1 min-w-0">
            <div className="text-sm font-semibold text-amber-200">
              {!meta?.last_refresh_at
                ? "La caché de FacturaDirecta nunca se ha sincronizado"
                : "La caché de FacturaDirecta está vacía"}
            </div>
            <p className="text-xs text-amber-100/80 mt-1">
              Los KPIs del Dashboard y de esta página leen de una caché local que se refresca automáticamente cada noche a las 03:00.
              Pulsa <b>Refrescar desde FD</b> para hacer una sincronización manual ahora mismo.
              Si tras refrescar sigues sin ver datos, comprueba que las facturas tengan fecha dentro del período seleccionado, o
              contacta con Emergent Support para revisar las variables <code className="text-[10px] bg-zinc-900 px-1 rounded">FACTURADIRECTA_API_KEY</code> /
              <code className="text-[10px] bg-zinc-900 px-1 rounded">FACTURADIRECTA_COMPANY_ID</code> en producción.
            </p>
          </div>
          <div className="flex flex-col gap-2 flex-shrink-0">
            <Button onClick={doRefresh} disabled={refreshing} size="sm"
                    data-testid="btn-tesoreria-refresh-empty"
                    className="bg-amber-500 hover:bg-amber-400 text-zinc-950 font-semibold">
              {refreshing ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <RefreshCw className="w-3 h-3 mr-1" />}
              Sincronizar ahora
            </Button>
            <Button onClick={checkFdHealth} disabled={checkingHealth} size="sm" variant="outline"
                    data-testid="btn-tesoreria-fd-health"
                    className="border-amber-500/40 text-amber-200 hover:bg-amber-500/10">
              {checkingHealth ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : null}
              Comprobar conexión FD
            </Button>
          </div>
        </div>
      )}
      {fdHealth && (
        <div className={`text-[11px] rounded-lg px-3 py-2 font-mono whitespace-pre-wrap ${fdHealth.connection?.ok ? "bg-emerald-500/10 border border-emerald-500/30 text-emerald-200" : "bg-rose-500/10 border border-rose-500/30 text-rose-200"}`}
             data-testid="fd-health-result">
          {JSON.stringify({
            configured: fdHealth.configured,
            connection_ok: fdHealth.connection?.ok,
            status_code: fdHealth.connection?.status_code,
            error: fdHealth.connection?.error,
            ms: fdHealth.connection?.ms,
            base_url: fdHealth.base_url,
            company_id: fdHealth.company_id,
            env: fdHealth.env,
          }, null, 2)}
        </div>
      )}

      {/* Filtros */}
      <div className="bg-zinc-900/40 border border-zinc-800 rounded-xl p-3 flex items-center gap-3 flex-wrap">
        <Filter className="w-4 h-4 text-zinc-500" />
        <div className="flex gap-1 flex-wrap">
          {PERIODOS.map(p => (
            <Button
              key={p.v}
              variant={periodo === p.v ? "default" : "outline"}
              size="sm"
              onClick={() => setPeriodo(p.v)}
              className={periodo === p.v ? "bg-emerald-500 hover:bg-emerald-600 text-zinc-900 text-xs h-8" : "text-xs h-8"}
              data-testid={`periodo-${p.v}`}
            >
              {p.label}
            </Button>
          ))}
        </div>
        <div className="flex items-center gap-2">
          <Label className="text-xs text-zinc-500">Cliente:</Label>
          <select
            value={clienteId}
            onChange={(e) => setClienteId(e.target.value)}
            className="bg-zinc-950 border border-zinc-700 rounded text-xs h-8 px-2 text-zinc-200"
            data-testid="filter-cliente"
          >
            <option value="">Todos</option>
            {clientes.map(c => <option key={c.id} value={c.id}>{c.nombre} ({c.n_facturas})</option>)}
          </select>
          {clienteId && (
            <Button variant="ghost" size="sm" onClick={() => setClienteId("")} className="h-8 text-xs">
              <IconX className="w-3 h-3" />
            </Button>
          )}
        </div>
      </div>

      {/* KPIs */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <KPI
          label="Facturado"
          value={k.total_facturado}
          icon={TrendingUp}
          tint="text-emerald-300"
          subtitle={
            variation !== null && (
              <span className={variation >= 0 ? "text-emerald-400" : "text-rose-400"}>
                {variation >= 0 ? "↑" : "↓"} {Math.abs(variation).toFixed(1)}% vs anterior
              </span>
            )
          }
          extra={`${k.count_facturas} factura${k.count_facturas !== 1 ? "s" : ""}`}
          testid="kpi-facturado"
        />
        <KPI
          label="Cobrado"
          value={k.total_cobrado}
          icon={CheckCircle2}
          tint="text-sky-300"
          subtitle={<span className="text-sky-400">{cobroRatio.toFixed(1)}% del facturado</span>}
          testid="kpi-cobrado"
        />
        <KPI
          label="Pendiente"
          value={k.total_pendiente}
          icon={Clock}
          tint="text-amber-300"
          subtitle={<span className="text-zinc-500">de cobrar</span>}
          testid="kpi-pendiente"
        />
        <KPI
          label="Vencido"
          value={k.total_vencido}
          icon={AlertTriangle}
          tint={k.total_vencido > 0 ? "text-rose-300" : "text-zinc-400"}
          subtitle={k.count_vencido > 0 ? <span className="text-rose-400">{k.count_vencido} facturas</span> : <span className="text-zinc-600">sin vencimientos</span>}
          testid="kpi-vencido"
        />
      </div>

      {/* Breakdown DNF vs externa */}
      <div className="bg-zinc-900/30 border border-zinc-800 rounded-xl p-3 flex flex-wrap items-center gap-4 text-xs">
        <Sparkles className="w-4 h-4 text-emerald-400" />
        <div>Emitidas via DNF: <b className="text-emerald-300">{euro(k.facturado_via_dnf)}</b> <span className="text-zinc-500">({k.via_dnf_count})</span></div>
        <div>Emitidas directas FD: <b className="text-sky-300">{euro(k.facturado_via_externa)}</b> <span className="text-zinc-500">({k.via_externa_count})</span></div>
      </div>

      {/* Gráficas */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <div className="lg:col-span-2 bg-zinc-900/40 border border-zinc-800 rounded-xl p-4" data-testid="chart-serie-mensual">
          <h3 className="text-sm font-bold text-zinc-200 mb-3">Facturación mensual <span className="text-xs text-zinc-500 font-normal">(últimos 12 meses, ignora periodo)</span></h3>
          {data.serie_mensual.length === 0 ? (
            <Empty msg="Sin datos en este periodo." />
          ) : (
            <ResponsiveContainer width="100%" height={260}>
              <BarChart data={data.serie_mensual}>
                <CartesianGrid strokeDasharray="3 3" stroke="#27272a" />
                <XAxis dataKey="mes" stroke="#a1a1aa" fontSize={10} />
                <YAxis stroke="#a1a1aa" fontSize={10} tickFormatter={(v) => `${(v / 1000).toFixed(0)}k`} />
                <Tooltip contentStyle={{ backgroundColor: "#18181b", border: "1px solid #3f3f46", borderRadius: 8 }} formatter={(v) => euro(v)} />
                <Legend wrapperStyle={{ fontSize: 11 }} />
                <Bar dataKey="cobrado" stackId="a" fill="#10b981" name="Cobrado" />
                <Bar dataKey="pendiente" stackId="a" fill="#f59e0b" name="Pendiente" />
              </BarChart>
            </ResponsiveContainer>
          )}
        </div>

        <div className="bg-zinc-900/40 border border-zinc-800 rounded-xl p-4" data-testid="chart-distribucion">
          <h3 className="text-sm font-bold text-zinc-200 mb-3">Distribución por estado</h3>
          {data.distribucion_estado.length === 0 ? (
            <Empty msg="Sin datos." />
          ) : (
            <ResponsiveContainer width="100%" height={260}>
              <PieChart>
                <Pie
                  data={data.distribucion_estado.map(e => ({ ...e, name: ESTADO_LABEL[e.estado] || e.estado }))}
                  dataKey="total"
                  nameKey="name"
                  cx="50%" cy="50%"
                  outerRadius={70}
                  label={(e) => `${(e.percent * 100).toFixed(0)}%`}
                  labelLine={false}
                  fontSize={10}
                >
                  {data.distribucion_estado.map((entry, idx) => (
                    <Cell key={idx} fill={ESTADO_COLORS[entry.estado] || "#52525b"} />
                  ))}
                </Pie>
                <Tooltip contentStyle={{ backgroundColor: "#18181b", border: "1px solid #3f3f46", borderRadius: 8 }} formatter={(v) => euro(v)} />
                <Legend wrapperStyle={{ fontSize: 10 }} />
              </PieChart>
            </ResponsiveContainer>
          )}
        </div>
      </div>

      {/* Top clientes */}
      <div className="bg-zinc-900/40 border border-zinc-800 rounded-xl p-4" data-testid="top-clientes">
        <h3 className="text-sm font-bold text-zinc-200 mb-3">Top 10 clientes del periodo</h3>
        {data.top_clientes.length === 0 ? (
          <Empty msg="Sin facturación en este periodo." />
        ) : (
          <div className="space-y-1">
            {data.top_clientes.map((c, i) => {
              const max = data.top_clientes[0].facturado || 1;
              const pct = (c.facturado / max) * 100;
              const isFiltered = clienteId === c.cliente_id;
              return (
                <button
                  key={c.cliente_id || i}
                  onClick={() => setClienteId(isFiltered ? "" : c.cliente_id)}
                  className={`w-full text-left p-2 rounded transition ${isFiltered ? "bg-emerald-500/10 border border-emerald-500/40" : "hover:bg-zinc-800/50"}`}
                  data-testid={`top-cliente-${i}`}
                >
                  <div className="flex items-center justify-between text-xs mb-1">
                    <span className="text-zinc-200 truncate flex items-center gap-2">
                      <span className="text-zinc-500 font-mono w-5">{i + 1}.</span>
                      <span className="font-semibold truncate">{c.nombre}</span>
                    </span>
                    <span className="text-emerald-300 font-mono shrink-0 ml-2">{euro(c.facturado)}</span>
                  </div>
                  <div className="w-full bg-zinc-800 h-1.5 rounded overflow-hidden">
                    <div className="h-full bg-gradient-to-r from-emerald-500 to-sky-500" style={{ width: `${pct}%` }} />
                  </div>
                  <div className="flex gap-3 text-[10px] text-zinc-500 mt-1">
                    <span>{c.n} facturas</span>
                    <span className="text-sky-400">Cobrado {euro(c.cobrado)}</span>
                    {c.pendiente > 0 && <span className="text-amber-400">Pendiente {euro(c.pendiente)}</span>}
                  </div>
                </button>
              );
            })}
          </div>
        )}
      </div>

      {/* Tabla facturas */}
      <div className="bg-zinc-900/40 border border-zinc-800 rounded-xl p-4" data-testid="tabla-facturas">
        <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
          <h3 className="text-sm font-bold text-zinc-200">Facturas del periodo ({facturas.length})</h3>
          <div className="flex gap-1 flex-wrap">
            {["", "paid", "pending", "overdue", "voided"].map(s => (
              <Button key={s} variant={estadoFiltro === s ? "default" : "outline"} size="sm" onClick={() => setEstadoFiltro(s)}
                className={`text-[10px] h-7 ${estadoFiltro === s ? "bg-zinc-200 text-zinc-900" : ""}`}
                data-testid={`filter-estado-${s || "todas"}`}>
                {s ? ESTADO_LABEL[s] : "Todas"}
              </Button>
            ))}
            {["", "dnf", "externa"].map(v => (
              <Button key={v} variant={viaFiltro === v ? "default" : "outline"} size="sm" onClick={() => setViaFiltro(v)}
                className={`text-[10px] h-7 ${viaFiltro === v ? "bg-emerald-500 text-zinc-900" : ""}`}
                data-testid={`filter-via-${v || "todas"}`}>
                {v === "dnf" ? "🔗 Via DNF" : v === "externa" ? "📋 Directa FD" : "Origen"}
              </Button>
            ))}
          </div>
        </div>
        {facturas.length === 0 ? (
          <Empty msg="Sin facturas que coincidan con los filtros." />
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-xs">
              <thead className="text-zinc-500 border-b border-zinc-800">
                <tr>
                  <th className="text-left py-2 px-2">Número</th>
                  <th className="text-left py-2 px-2">Fecha</th>
                  <th className="text-left py-2 px-2">Cliente</th>
                  <th className="text-right py-2 px-2">Total</th>
                  <th className="text-right py-2 px-2">Cobrado</th>
                  <th className="text-left py-2 px-2">Estado</th>
                  <th className="text-left py-2 px-2">Vence</th>
                  <th className="text-left py-2 px-2">Origen</th>
                </tr>
              </thead>
              <tbody>
                {facturas.map(f => (
                  <FacturaRow key={f.fd_id} f={f} />
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

function KPI({ label, value, icon: Icon, tint, subtitle, extra, testid }) {
  return (
    <div className="bg-zinc-900/60 border border-zinc-800 rounded-xl p-4" data-testid={testid}>
      <div className="flex items-center justify-between text-xs text-zinc-500 mb-1">
        <span>{label}</span>
        {Icon && <Icon className="w-4 h-4 text-zinc-600" />}
      </div>
      <div className={`text-2xl font-bold ${tint}`}>{euro(value)}</div>
      {subtitle && <div className="text-[10px] mt-1">{subtitle}</div>}
      {extra && <div className="text-[10px] text-zinc-600 mt-0.5">{extra}</div>}
    </div>
  );
}

function FacturaRow({ f }) {
  const diasVencida = f.fd_state === "overdue" && f.fd_due_date
    ? Math.floor((Date.now() - new Date(f.fd_due_date).getTime()) / 86400000)
    : null;
  return (
    <tr className="border-b border-zinc-800/50 hover:bg-zinc-800/30" data-testid={`fact-row-${f.fd_id}`}>
      <td className="py-2 px-2 font-mono text-amber-300">{f.fd_numero}</td>
      <td className="py-2 px-2 text-zinc-300">{f.fd_date}</td>
      <td className="py-2 px-2 text-zinc-200">{f.fd_contact_name || "—"}</td>
      <td className="py-2 px-2 text-right font-mono">{euro(f.fd_total)}</td>
      <td className="py-2 px-2 text-right font-mono text-sky-300">{euro(f.fd_paid_amount)}</td>
      <td className="py-2 px-2">
        <span className="px-1.5 py-0.5 rounded text-[10px] font-semibold" style={{ backgroundColor: `${ESTADO_COLORS[f.fd_state]}20`, color: ESTADO_COLORS[f.fd_state] }}>
          {ESTADO_LABEL[f.fd_state] || f.fd_state}
        </span>
      </td>
      <td className="py-2 px-2 text-zinc-400">
        {f.fd_due_date}
        {diasVencida > 0 && <span className="text-rose-400 ml-1">(+{diasVencida}d)</span>}
      </td>
      <td className="py-2 px-2">
        {f.dnf_via_app ? (
          <a href={`/peticiones/${f.dnf_peticion_id}`} className="text-emerald-400 text-[10px] hover:underline" data-testid={`open-pet-${f.fd_id}`}>🔗 Via DNF</a>
        ) : (
          <span className="text-zinc-500 text-[10px]">📋 Directa FD</span>
        )}
      </td>
    </tr>
  );
}

function Empty({ msg }) {
  return <div className="text-center text-zinc-500 py-8 text-sm">{msg}</div>;
}

function euro(v) {
  return new Intl.NumberFormat("es-ES", { style: "currency", currency: "EUR", maximumFractionDigits: 2 }).format(v || 0);
}
